/**
 * Error banner for failed uploads
 * 
 * Shows error reasons and allows retry/dismiss actions.
 */

import { useState } from 'react';
import type { FailedUpload } from '../types';

interface ErrorBannerProps {
  failedUploads: FailedUpload[];
  onRetry: () => void;
  onDismiss: () => void;
}

const MAX_FILENAMES_SHOWN = 3;

export function ErrorBanner({ failedUploads, onRetry, onDismiss }: ErrorBannerProps) {
  const [showDetails, setShowDetails] = useState(false);

  if (failedUploads.length === 0) {
    return null;
  }

  // Get the first error reason (most useful for user)
  const firstError = failedUploads[0]?.error || 'Unknown error';
  
  // Group by error reason to show summary
  const errorCounts = failedUploads.reduce((acc, f) => {
    acc[f.error] = (acc[f.error] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  
  const uniqueErrors = Object.keys(errorCounts);
  const hasMultipleErrorTypes = uniqueErrors.length > 1;

  // Format filename list
  const filenames = failedUploads.map((f) => f.filename);
  const displayedFilenames = filenames.slice(0, MAX_FILENAMES_SHOWN);
  const remainingCount = filenames.length - MAX_FILENAMES_SHOWN;

  return (
    <div
      style={{
        padding: '12px 16px',
        backgroundColor: '#fef2f2',
        border: '1px solid #fecaca',
        borderRadius: '8px',
        marginBottom: '16px',
        fontSize: '14px',
        color: '#991b1b',
      }}
    >
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div style={{ flex: 1 }}>
          {/* Main message */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <span style={{ fontSize: '16px' }}>!</span>
            <span style={{ fontWeight: 600 }}>
              {failedUploads.length} upload{failedUploads.length > 1 ? 's' : ''} failed
            </span>
          </div>
          
          {/* Error reason */}
          <div style={{ fontSize: '13px', color: '#b91c1c', marginBottom: '8px' }}>
            {hasMultipleErrorTypes ? (
              <span>Multiple errors occurred</span>
            ) : (
              <span>Reason: {firstError}</span>
            )}
          </div>

          {/* File list (collapsed by default) */}
          {!showDetails ? (
            <div style={{ fontSize: '13px', color: '#7f1d1d' }}>
              <span>
                {displayedFilenames.join(', ')}
                {remainingCount > 0 && ` and ${remainingCount} more`}
              </span>
              {failedUploads.length > MAX_FILENAMES_SHOWN && (
                <button
                  onClick={() => setShowDetails(true)}
                  style={{
                    marginLeft: '8px',
                    padding: '2px 8px',
                    backgroundColor: 'transparent',
                    border: '1px solid #fca5a5',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    color: '#991b1b',
                  }}
                >
                  Show all
                </button>
              )}
            </div>
          ) : (
            <div style={{ fontSize: '13px', color: '#7f1d1d' }}>
              <div style={{ marginBottom: '8px' }}>
                {failedUploads.map((f, i) => (
                  <div key={i} style={{ marginBottom: '2px' }}>
                    <span style={{ fontWeight: 500 }}>{f.filename}</span>
                    {hasMultipleErrorTypes && (
                      <span style={{ marginLeft: '8px', fontSize: '12px', color: '#b91c1c' }}>
                        ({f.error})
                      </span>
                    )}
                  </div>
                ))}
              </div>
              <button
                onClick={() => setShowDetails(false)}
                style={{
                  padding: '2px 8px',
                  backgroundColor: 'transparent',
                  border: '1px solid #fca5a5',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  color: '#991b1b',
                }}
              >
                Show less
              </button>
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: '8px', marginLeft: '16px' }}>
          <button
            onClick={onRetry}
            style={{
              padding: '6px 14px',
              backgroundColor: '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 500,
            }}
          >
            Retry
          </button>
          <button
            onClick={onDismiss}
            style={{
              padding: '6px 10px',
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontSize: '18px',
              color: '#991b1b',
              lineHeight: 1,
            }}
            aria-label="Dismiss"
          >
            x
          </button>
        </div>
      </div>
    </div>
  );
}
